import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Spinner, Label } from 'reactstrap';
import { Edit, Trash2, Plus, RefreshCw } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn } from '../../../../AbstractElements';
import UserService from '../../../../Api/services/user.service';
import type { components } from '../../../../Api/types/openapi';
import UserFormModal from './UserFormModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { useTranslation } from 'react-i18next';

type UserSimpleResponse = components['schemas']['UserSimpleResponse'];
type PaginationUserSimpleResponse = components['schemas']['Pagination_UserSimpleResponse_'];

const UsersList = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserSimpleResponse[]>([]);
  const [pagination, setPagination] = useState<PaginationUserSimpleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSimpleResponse | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [currentPage, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit,
      };

      if (searchTerm) {
        params.name = searchTerm;
      }

      const data = await UserService.getUsersPaginated(params);
      
      // VALIDAR que la respuesta tenga results
      if (!data || !Array.isArray(data.results)) {
        console.warn('Respuesta inválida del servidor: results no es un array');
        setUsers([]);
        setPagination(null);
        return;
      }
      
      setUsers(data.results);
      setPagination(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar usuarios';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (user: UserSimpleResponse) => {
    setSelectedUser(user);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = (user: UserSimpleResponse) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser?.id) return;

    try {
      await UserService.deleteUser(selectedUser.id);
      toast.success('Usuario eliminado exitosamente');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al eliminar usuario';
      toast.error(errorMessage);
    }
  };

  const handleToggleState = async (user: UserSimpleResponse) => {
    if (!user.id) return;

    try {
      await UserService.changeUserState(user.id);
      toast.success(`Usuario ${user.active ? 'desactivado' : 'activado'} exitosamente`);
      loadUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cambiar estado';
      toast.error(errorMessage);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedUser(null);
    setIsEditing(false);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    loadUsers();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= pagination.pages; i++) {
      pages.push(
        <Btn
          key={i}
          color={i === currentPage ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setCurrentPage(i)}
          className="me-1"
        >
          {i}
        </Btn>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <span className="text-muted">
            Página {pagination.current_page} de {pagination.pages} ({pagination.results.length} resultados)
          </span>
        </div>
        <div>{pages}</div>
      </div>
    );
  };

  if (loading && users.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <>
      <Row className="mb-3">
        <Col md="6">
          <Input
            type="text"
            placeholder={t('Buscar por nombre...')}
            value={searchTerm}
            onChange={handleSearch}
          />
        </Col>
        <Col md="6" className="text-end">
          <Btn color="primary" onClick={handleCreate} className="me-2">
            <Plus size={16} className="me-1" />
            {t("Nuevo Usuario")}
          </Btn>
          <Btn color="secondary" onClick={loadUsers}>
            <RefreshCw size={16} className="me-1" />
            {t("Actualizar")}
          </Btn>
        </Col>
      </Row>

      <div className="table-responsive theme-scrollbar">
        <Table hover responsive>
          <thead>
            <tr>
              <th>ID</th>
              <th>{t("Nombre")}</th>
              <th>{t("Email")}</th>
              <th>{t("Teléfono")}</th>
              <th>{t("Rol")}</th>
              <th>{t("Estado")}</th>
              <th>{t("Acciones")}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">
                  {t("No se encontraron usuarios")}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.name || '-'}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    {user.country_code_phone && user.phone
                      ? `${user.country_code_phone} ${user.phone}`
                      : '-'}
                  </td>
                  <td>
                    {user.role?.name || '-'}
                  </td>
                  <td>
                    <div className="form-check form-switch">
                      <Input
                        className="form-check-input"
                        type="checkbox"
                        id={`switch-${user.id}`}
                        checked={user.active || false}
                        onChange={() => handleToggleState(user)}
                      />
                      <Label className="form-check-label" htmlFor={`switch-${user.id}`}>
                        {user.active ? t("Activo") : t("Inactivo")}
                      </Label>
                    </div>
                  </td>
                  <td>
                    <Btn
                      color="primary"
                      size="sm"
                      className="me-1 btn_list_action"
                      onClick={() => handleEdit(user)}
                    >
                      <Edit size={14} />
                    </Btn>
                    <Btn
                      color="danger"
                      size="sm"
                      className="btn_list_action"
                      onClick={() => handleDelete(user)}
                    >
                      <Trash2 size={14} />
                    </Btn>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {renderPagination()}

      <UserFormModal
        isOpen={showModal}
        toggle={handleModalClose}
        user={selectedUser}
        isEditing={isEditing}
        onSuccess={handleModalSuccess}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default UsersList;

